import localforage from 'localforage'
import type { 
  GameState, 
  SaveMeta, 
  PersistenceAdapter, 
  ImportResult 
} from '../persistence-contract.js'

export class LocalPersistenceAdapter implements PersistenceAdapter {
  private LOCAL_SAVE_PREFIX = 'local_save_'
  private LOCAL_SAVE_INDEX_PREFIX = 'local_saves_index_'

  constructor() {
    localforage.config({
      name: 'AnyQuest',
      storeName: 'vision_cache',
    })
  }

  private localSaveKey(uid: string, saveId: string) {
    return `${this.LOCAL_SAVE_PREFIX}${uid}_${saveId}`
  }

  private localSaveIndexKey(uid: string) {
    return `${this.LOCAL_SAVE_INDEX_PREFIX}${uid}`
  }

  async saveGame(uid: string, saveId: string, state: GameState): Promise<void> {
    const key = this.localSaveKey(uid, saveId)
    await localforage.setItem(key, {
      uid,
      saveId,
      lastStateTimestamp: state.meta.timestamp ?? Date.now(),
      stateString: JSON.stringify(state),
      compressed: false
    })

    // Update index
    const indexKey = this.localSaveIndexKey(uid)
    const index = await localforage.getItem<string[]>(indexKey) ?? []
    if (!index.includes(saveId)) {
      index.push(saveId)
      await localforage.setItem(indexKey, index)
    }
  }

  async loadGame(uid: string, saveId: string): Promise<GameState | null> {
    const key = this.localSaveKey(uid, saveId)
    const entry = await localforage.getItem<{ stateString: string }>(key)
    if (!entry) return null
    return JSON.parse(entry.stateString) as GameState
  }

  async deleteGame(uid: string, saveId: string): Promise<void> {
    const key = this.localSaveKey(uid, saveId)
    await localforage.removeItem(key)

    const indexKey = this.localSaveIndexKey(uid)
    const index = await localforage.getItem<string[]>(indexKey) ?? []
    const nextIndex = index.filter(id => id !== saveId)
    await localforage.setItem(indexKey, nextIndex)
  }

  async listSaves(uid: string): Promise<SaveMeta[]> {
    const indexKey = this.localSaveIndexKey(uid)
    const index = await localforage.getItem<string[]>(indexKey) ?? []
    const results: SaveMeta[] = []
    
    for (const saveId of index) {
      const state = await this.loadGame(uid, saveId)
      if (state) {
        results.push({
          id: saveId,
          state: state
        })
      }
    }
    return results
  }

  async importByCode(code: string): Promise<ImportResult> {
    // Local-only doesn't support public codes easily without a registry
    return { type: 'not_found' }
  }

  async exportArtifact(code: string, payload: string, type: string): Promise<void> {
    // No-op or save to local storage for persistence
  }
}
